import { useRouter } from 'next/router';
import Head from 'next/head';
import _get from 'lodash/get';
import { useQuery } from '@apollo/client';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/error-message';
import NotFound from '../../../components/not-found';
import EventList from '../../../components/event-list';
import { eventSeriesQuery } from '../../../apollo/event-series';
import { buildTitle } from '../../../utils/layout';
import config from '../../../config';
import { buildJsonLdEvents } from '../../../json-ld/event';
import Main from '../../../components/main';
import Header from '../../../components/event-series/header';
import MaterialLinkButton from '../../../components/material-link-button';
import { eventSeriesScheduleSummary } from '../../../utils/event-series-schedule';
import Markdown from '../../../components/markdown';
import HostSummaries from '../../../components/host-summaries';

const useStyles = makeStyles((theme) => ({
  eventsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  eventsHeaderRight: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  scheduleMoreButton: {
    marginLeft: theme.spacing(1),
  },
}));

const EventSeriesPage = () => {
  const router = useRouter();
  const classes = useStyles();

  const { data, loading, error } = useQuery(eventSeriesQuery, {
    variables: {
      id: router.query.id,
    },
  });
  const eventSeries = _get(data, 'eventSeries');

  if (error) {
    return <ErrorMessage message='An error occurred while loading event series.' error={error} />;
  }
  if (loading) { return <Loading />; }
  if (!eventSeries) { return <NotFound />; }

  const title = eventSeries.name;
  const description = `Event series by ${eventSeries.hostType === 'Group' ? eventSeries.group.name : eventSeries.user.displayName}`;
  const url = `${config().universal.baseUri}/event-series/${eventSeries.id}`;

  return (
    <Main>
      <Head>
        <title>{buildTitle(title)}</title>
        <link rel="canonical" href={url} />
        {eventSeries.upcomingEvents.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(buildJsonLdEvents(eventSeries.upcomingEvents))
            }}
          />
        )}
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="description" content={description} />
      </Head>

      <Header eventSeries={eventSeries} tab="overview" />

      {eventSeries.description && (
        <div className={classes.description}>
          <Markdown>{eventSeries.description}</Markdown>
        </div>
      )}

      <div className={classes.schedule}>
        {eventSeriesScheduleSummary(eventSeries)}
      </div>

      <Typography variant="h2" className={classes.eventsHeader}>
        <span>Upcoming</span>
        <div className={classes.eventsHeaderRight}>
          <MaterialLinkButton
            href="/event-series/[id]/past"
            as={`/event-series/${eventSeries.id}/past`}
            variant="outlined"
            size="small"
          >
            Past ({eventSeries.pastEventsCount})
          </MaterialLinkButton>
          {eventSeries.permissions.editable && (
            <MaterialLinkButton
              href="/event-series/[id]/schedule"
              as={`/event-series/${eventSeries.id}/schedule`}
              variant="contained"
              color="primary"
              size="small"
              className={classes.scheduleMoreButton}
            >
              Schedule Events
            </MaterialLinkButton>
          )}
        </div>
      </Typography>
      <EventList
        events={eventSeries.upcomingEvents}
        emptyMessage="No upcoming events"
        fullDate
      />
      
      <HostSummaries event={eventSeries} />
    </Main>
  );
};

export default EventSeriesPage;
